import React, { useState, useEffect } from 'react';
import { MessageSquare } from 'lucide-react';
import axios from 'axios';

interface PredefinedMessage {
  id: string;
  text: string;
  category: string;
}

interface PredefinedMessagesProps {
  onSelectMessage: (message: string) => void;
  disabled?: boolean;
  bookTitle?: string;
  otherUserName?: string;
}

const PredefinedMessages: React.FC<PredefinedMessagesProps> = ({ 
  onSelectMessage, 
  disabled = false, 
  bookTitle = '', 
  otherUserName = '' 
}) => {
  const [messages, setMessages] = useState<PredefinedMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Messages prédéfinis façon Vinted avec variables
  const defaultMessages: PredefinedMessage[] = [
    // Salutations
    { id: 'greet-1', text: 'Bonjour {firstName} ! 👋', category: 'greeting' },
    { id: 'greet-2', text: 'Salut ! Intéressé par "{bookTitle}" 📚', category: 'greeting' },
    { id: 'greet-3', text: 'Hello ! Ça vous dit pour "{bookTitle}" ?', category: 'greeting' },
    
    // Disponibilité
    { id: 'avail-1', text: 'Quand êtes-vous disponible ? ⏰', category: 'availability' },
    { id: 'avail-2', text: 'Quels sont vos créneaux cette semaine ?', category: 'availability' },
    { id: 'avail-3', text: 'Disponible ce weekend ?', category: 'availability' },
    
    // Lieu
    { id: 'loc-1', text: 'Où souhaitez-vous qu\'on se rencontre ? 📍', category: 'location' },
    { id: 'loc-2', text: 'Métro {place} ça vous va ?', category: 'location' },
    { id: 'loc-3', text: 'Chez vous ou chez moi ?', category: 'location' },
    
    // Proposition de RDV
    { id: 'prop-1', text: 'RDV {date} à {time} ?', category: 'proposal' },
    { id: 'prop-2', text: 'Ça vous va {date} vers {time} ?', category: 'proposal' },
    { id: 'prop-3', text: 'Je propose {date} à {time} à {place}', category: 'proposal' },
    
    // Confirmation
    { id: 'conf-1', text: 'Parfait ! À {date} à {time} alors ✅', category: 'confirmation' },
    { id: 'conf-2', text: 'Confirmé ! Je vous envoie l\'adresse', category: 'confirmation' },
    { id: 'conf-3', text: 'Super ! À bientôt pour "{bookTitle}"', category: 'confirmation' },
    
    // Retard/Report
    { id: 'delay-1', text: 'Désolé, je vais être en retard de 15min', category: 'delay' },
    { id: 'delay-2', text: 'Peut-on reporter à demain ?', category: 'delay' },
    { id: 'delay-3', text: 'Urgence, on peut décaler ?', category: 'delay' },
    
    // Annulation
    { id: 'cancel-1', text: 'Finalement, je ne peux plus', category: 'cancel' },
    { id: 'cancel-2', text: 'Désolé, je dois annuler', category: 'cancel' },
    { id: 'cancel-3', text: 'Ça ne va plus être possible', category: 'cancel' }
  ];

  const fetchPredefinedMessages = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get('/api/chat/predefined-messages');
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching predefined messages, using defaults:', error);
      setMessages(defaultMessages);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPredefinedMessages();
  }, []);

  const handleMessageSelect = (message: string) => {
    // Remplacer les variables si disponibles
    let personalizedMessage = message;
    if (otherUserName) {
      const firstName = otherUserName.split(' ')[0];
      personalizedMessage = personalizedMessage.replace('{firstName}', firstName);
    }
    if (bookTitle) {
      personalizedMessage = personalizedMessage.replace('{bookTitle}', bookTitle);
    }
    
    onSelectMessage(personalizedMessage);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'greeting':
        return '👋';
      case 'location':
        return '📍';
      case 'availability':
        return '⏰';
      case 'proposal':
        return '📅';
      case 'confirmation':
        return '✅';
      case 'delay':
        return '⏳';
      case 'cancel':
        return '❌';
      default:
        return '💬';
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'greeting':
        return 'Salutation';
      case 'location':
        return 'Lieu';
      case 'availability':
        return 'Disponibilité';
      case 'proposal':
        return 'Proposition';
      case 'confirmation':
        return 'Confirmation';
      case 'delay':
        return 'Retard';
      case 'cancel':
        return 'Annulation';
      default:
        return 'Autre';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'greeting':
        return 'bg-forest-50 text-forest-700 border-forest-200';
      case 'location':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'availability':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'proposal':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'confirmation':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'delay':
        return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'cancel':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  // Grouper les messages par catégorie
  const groupedMessages = messages.reduce((acc, message) => {
    if (!acc[message.category]) {
      acc[message.category] = [];
    }
    acc[message.category].push(message);
    return acc;
  }, {} as Record<string, PredefinedMessage[]>);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-2 text-gray-500">
        <div className="animate-spin rounded-full h-4 w-4 border-2 border-stone-200 border-t-forest-600"></div>
        <span className="ml-2 text-sm">Chargement...</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center space-x-2 text-sm text-gray-600">
        <MessageSquare className="h-4 w-4" />
        <span>Messages rapides</span>
      </div>
      
      <div className="space-y-3">
        {Object.entries(groupedMessages).map(([category, categoryMessages]) => (
          <div key={category} className="space-y-2">
            <div className="flex items-center space-x-2">
              <span className="text-lg">{getCategoryIcon(category)}</span>
              <span className="text-xs font-medium text-gray-500">
                {getCategoryLabel(category)}
              </span>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {categoryMessages.map((message) => (
                <button
                  key={message.id}
                  onClick={() => handleMessageSelect(message.text)}
                  disabled={disabled}
                  className={`px-3 py-2 rounded-full text-xs font-medium border transition-colors hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${getCategoryColor(message.category)}`}
                >
                  {message.text}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PredefinedMessages;
